import { INITIAL, MOVERS } from './movers'

/**
 * The film writer, called from the single rAF of useFilmLoop (or once, for a
 * still) with the film's root element. It writes `data-station` on the root,
 * the ruler fill's transform and, for every element the film moves or fades,
 * its inline transform / opacity / clip-path (movers.js). It never writes
 * the --pp-* primitives on a shared ancestor: a registered, inherited
 * property that changes there restyles the whole set every frame; written
 * per element, a frame restyles only what changed. A value is touched only
 * when its formatted string changed. No React state, no layout reads; the
 * elements are looked up once per drawing.
 */

const bindings = new WeakMap()

function bind(root) {
  const cached = bindings.get(root)
  if (cached && cached.cam.isConnected) return cached
  const box = root.querySelector('.pp__stage-box')
  const cam = box?.querySelector('.pp-cam')
  // The drawing mounts near the viewport; until then there is nothing to bind.
  if (!cam) return null
  const movers = MOVERS.flatMap(([selector, styles, prep]) =>
    Array.from(box.querySelectorAll(selector), (el) => ({
      style: el.style,
      styles,
      ctx: prep ? prep(el) : null,
      last: {},
    })),
  )
  const fill = root.querySelector('.pp__ruler-fill')
  const bound = { cam, movers, fill: fill && { style: fill.style, last: {} } }
  bindings.set(root, bound)
  return bound
}

/* Write only on change; `last` remembers what was written (cheaper and more
   reliable than reading a normalised value back). */
function write(style, last, prop, text) {
  if (last[prop] === text) return
  style.setProperty(prop, text)
  last[prop] = text
}

const kebab = (prop) => prop.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)

/**
 * Draw one frame of a film.
 *
 * @param {HTMLElement} root the film (`.pp-film`)
 * @param {{ vars: Record<string, number>, station: number, fill?: [number, number] }} frame
 */
export function writeFrame(root, { vars: set, station, fill = [0, 1] }) {
  const next = String(station)
  if (root.dataset.station !== next) root.dataset.station = next
  const bound = bind(root)
  if (!bound) return
  const vars = { ...INITIAL, ...set }
  if (bound.fill) {
    const [from, to] = fill
    write(
      bound.fill.style,
      bound.fill.last,
      'transform',
      `translateX(${(from * 100).toFixed(2)}%) scaleX(${Math.max(0, to - from).toFixed(4)})`,
    )
  }
  bound.movers.forEach(({ style, styles, ctx, last }) => {
    Object.entries(styles(vars, ctx)).forEach(([prop, text]) => write(style, last, kebab(prop), text))
  })
}
